import { Circle, MapContainer, TileLayer } from 'react-leaflet'
import { calculateDistanceBetweenTwoPoints } from 'utils/map'
import { CheckingLocationPopupChildProps, Status } from '.'
import React, { useEffect, useRef, useState } from 'react'
import ArrowBtn from 'components/buttons/ArrowBtn'
import { selectLightTheme } from 'appSlice'
import { useReduxSelector } from 'store'
import type { Map } from 'leaflet'
import Popup from '../Popup'

const CheckingLocationPopupWithMap: React.FC<CheckingLocationPopupChildProps> = ({ distanceAccuracy, targetPosition, callback }) => {
  const isLightTheme = useReduxSelector(selectLightTheme)
  const [location, setLocation] = useState<GeolocationCoordinates | null>(null)
  const [status, setStatus] = useState<Status>('idle')
  const [isReady, setReady] = useState(false)

  const currentTarget = useRef(targetPosition)
  currentTarget.current = targetPosition
  const currentCallback = useRef(callback)
  currentCallback.current = callback
  const map = useRef<Map | null>(null)

  useEffect(() => {
    if (status !== 'idle') return
    let settled = false
    const watchId = navigator.geolocation.watchPosition(
      pos => {
        setLocation(pos.coords)
        if (!settled && pos.coords.accuracy <= distanceAccuracy) {
          settled = true
          navigator.geolocation.clearWatch(watchId)
          if (currentTarget.current) {
            const distance = calculateDistanceBetweenTwoPoints(pos.coords, { latitude: currentTarget.current.lat, longitude: currentTarget.current.lng })
            setStatus(distance <= distanceAccuracy ? 'pending-succeed' : 'pending-failed')
          } else {
            setStatus('pending-failed')
          }
        }
      },
      err => {
        setStatus('pending-failed')
        console.error(err)
      },
      { enableHighAccuracy: true }
    )

    const timeoutId = setTimeout(() => {
      if (!settled) {
        navigator.geolocation.clearWatch(watchId)
      }
    }, 15_000)

    return () => {
      navigator.geolocation.clearWatch(watchId)
      clearTimeout(timeoutId)
    }
  }, [distanceAccuracy, status])

  useEffect(() => {
    if (isReady && location) {
      map.current?.flyTo({ lat: location.latitude, lng: location.longitude }, 14)
    }
  }, [isReady, location])

  useEffect(() => {
    if (status === 'succeed' || status === 'failed') {
      const timeout = setTimeout(() => {
        currentCallback.current(status === 'succeed')
      }, 3_000)
      return () => {
        clearTimeout(timeout)
      }

    } else if (status === 'pending-succeed' || status === 'pending-failed') {
      const timeout = setTimeout(() => {
        setStatus(status === 'pending-failed' ? 'failed' : 'succeed')
      }, 5_000)
      return () => {
        clearTimeout(timeout)
      }
    }
  }, [status])

  return (
    <Popup>
      <div className='popup__checkingLocation --map'>
        <h3>Trwa ustalanie Twojej lokalizacji</h3>
        <div
          className='map__leaflet'
          onTouchStart={e => {
            e.stopPropagation()
          }}>
          <MapContainer
            zoom={6}
            minZoom={3}
            maxZoom={18}
            scrollWheelZoom={true}
            center={targetPosition}
            whenCreated={mapRef => {
              map.current = mapRef
              setReady(true)
            }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
              url={`https://{s}.basemaps.cartocdn.com/${isLightTheme ? 'light' : 'dark'}_all/{z}/{x}/{y}{r}.png`}
              subdomains="abcd"
            />
            {location && <Circle
              center={{
                lat: location.latitude,
                lng: location.longitude
              }}
              radius={location.accuracy}
              pathOptions={{ fillColor: 'none', color: 'var(--primary-color)' }}
            />}
          </MapContainer>
        </div>
        <ArrowBtn
          enlarged
          showArrow={false}
          text={status === 'failed'
            ? 'Sprawdzanie nie powiodło się!'
            : status === 'succeed'
              ? 'Uruchamianie akcji...'
              : 'Sprawdzanie lokalizacji...'}
          action={() => { }}
        />
      </div>
    </Popup>
  )
}

export default CheckingLocationPopupWithMap